import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Copy, Check, ArrowRight, Twitter, Linkedin, Share2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import PraiseLogo from "@/components/PraiseLogo";

const Share = () => {
  const { id } = useParams();
  const [copied, setCopied] = useState(false);
  const [testimonial, setTestimonial] = useState<any>(null);
  const [transformations, setTransformations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const { data: testimonialData } = await supabase
        .from("testimonials")
        .select("*")
        .eq("id", id)
        .maybeSingle();

      const { data: transformData } = await supabase
        .from("transformations")
        .select("*")
        .eq("testimonial_id", id)
        .order("created_at", { ascending: true });

      setTestimonial(testimonialData);
      setTransformations(transformData || []);
    } catch (error) {
      console.error("Error fetching:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const copyContent = async (content: string) => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareToTwitter = () => {
    const text = encodeURIComponent(`Check out this AI-transformed testimonial from Praise!\n\n${window.location.href}`);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, "_blank");
  };

  const shareToLinkedIn = () => {
    const url = encodeURIComponent(window.location.href);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, "_blank");
  };

  const formatType = (type: string) => type.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-brand-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!testimonial) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">Transformation not found</h1>
          <Link to="/"><Button>Go Home</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <PraiseLogo size={28} />
            <span className="text-xl font-bold gradient-brand-text">Praise</span>
          </Link>
          <Link to="/auth">
            <Button size="sm" className="gradient-brand text-white">Try Free</Button>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto py-12 px-4">
        <div className="text-center mb-8">
          <Badge variant="outline" className="mb-4">Shared Transformation</Badge>
          <h1 className="text-3xl font-bold text-foreground mb-2">{testimonial.client_name}</h1>
          <p className="text-muted-foreground">{testimonial.brand_name || "Testimonial"}</p>
        </div>

        {transformations.length > 0 ? (
          <div className="space-y-6 mb-8">
            {transformations.map(t => (
              <Card key={t.id} className="border-border">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">{formatType(t.output_type)}</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => copyContent(t.content)}>
                    {copied ? <><Check className="mr-2 h-4 w-4" />Copied</> : <><Copy className="mr-2 h-4 w-4" />Copy</>}
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="bg-muted p-6 rounded-lg">
                    <pre className="whitespace-pre-wrap font-sans text-foreground text-sm">{t.content}</pre>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="mb-8">
            <CardContent className="py-12 text-center text-muted-foreground">
              No transformations available for this testimonial.
            </CardContent>
          </Card>
        )}

        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <Button variant="outline" onClick={shareToTwitter}><Twitter className="mr-2 h-4 w-4" />Share on X</Button>
          <Button variant="outline" onClick={shareToLinkedIn}><Linkedin className="mr-2 h-4 w-4" />Share on LinkedIn</Button>
          <Button variant="outline" onClick={() => copyContent(window.location.href)}><Share2 className="mr-2 h-4 w-4" />Copy Link</Button>
        </div>

        <Card className="gradient-brand border-0">
          <CardContent className="py-12 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Create Your Own Transformations</h2>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">
              Turn any testimonial into 6+ marketing assets with AI. Start with 3 free credits — no credit card required.
            </p>
            <Link to="/auth">
              <Button size="lg" className="bg-white text-brand-600 hover:bg-white/90">
                Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        <div className="text-center mt-8">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <PraiseLogo size={16} />
            <span className="text-sm">Made with Praise</span>
          </Link>
        </div>
      </main>
    </div>
  );
};

export default Share;
